import { FC } from "react";
import { EnterpriseAccountsStore } from "./enterpriseAccountsDomain";
import { observer } from "mobx-react";
import { useTranslation } from "../stores/RootStoreContext";
import { Box, Container, VBox } from "../components-v2/Box";
import { isFailure, isOk, isPending } from "../common/common";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { Alert } from "../components-v2/Alert";
import { EmptyState } from "../components-v2/EmptyState";
import { Icon } from "../components-v2/Icon";
import { DropdownMenu } from "../components-v2/Dropdown";
import { LinkButton } from "../components-v2/Button";
import { loadUrl } from "../events/eventsDomain";
import { SharepointSettings, SharepointSettingsDialog } from "./sharepointComponents";

export const Integrations: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  return (
    <Container marginTop={"md"} maxWidth={"xl"}>
      {store.integrationSettingsModel && <AddOrUpdateIntegrationSettingsDialog store={store} />}
      {isPending(store.sharepointSettingsResult) && <LoadingIndicator />}
      {isFailure(store.sharepointSettingsResult) && (
        <Alert type={"error"} message={t("general-error")} />
      )}
      {isOk(store.sharepointSettingsResult) &&
        (store.sharepointSettingsResult.data === undefined ? (
          <IntegrationEmptyState store={store} />
        ) : (
          <IntegrationSettings store={store} />
        ))}
    </Container>
  );
});

const IntegrationEmptyState: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  return (
    <Container padding gap={"lg"} maxWidth={"xl"} grow={"golden"} justifyContent={"center"}>
      <EmptyState
        illustration={<Icon name={"plugs"} size={"xxl"} duotone />}
        title={{ key: "integrations.no-integrations.title" }}
        description={{ key: "integrations.no-integrations.description" }}
        actions={
          <VBox>
            <DropdownMenu
              button={{
                type: "button",
                label: { key: "integrations.add-integration" },
                variant: "secondary",
              }}
              items={[
                {
                  type: "item",
                  label: { key: "integrations.add-sharepoint" },
                  icon: "plugs",
                  onSelect: () => store.toggleSharepointSettingsDialog(),
                },
              ]}
            />
            <Box justifyContent={"center"} marginTop={"lg"}>
              <LinkButton
                size={"sm"}
                icon={"info"}
                label={t("integrations.documentation")}
                weight={"normal"}
                onClick={[loadUrl("https://dcompany.no/funksjoner/integrations")]}
              />
            </Box>
          </VBox>
        }
      />
    </Container>
  );
});

const AddOrUpdateIntegrationSettingsDialog: FC<{ store: EnterpriseAccountsStore }> = observer(
  ({ store }) => {
    if (store.integrationSettingsModel?.type === "sharepoint") {
      return <SharepointSettingsDialog store={store} />;
    }
    return <div />;
  },
);

const IntegrationSettings: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  return (
    <Container padding gap={"lg"} maxWidth={"lg"} grow={"golden"} justifyContent={"center"}>
      <Box justifyContent={"space-between"}>
        <VBox padding={false}>
          <h4>{t("enterprise-account.integrations")}</h4>
          {t("integrations.description")}
        </VBox>
        <Box>
          <LinkButton
            size={"sm"}
            icon={"info"}
            label={t("integrations.documentation")}
            weight={"normal"}
            onClick={[loadUrl("https://dcompany.no/funksjoner/integrations")]}
          />
        </Box>
      </Box>
      {isOk(store.sharepointSettingsResult) && <SharepointSettings store={store} />}
    </Container>
  );
});
